import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import Collapse from '@mui/material/Collapse';
import { TransitionGroup } from 'react-transition-group';
import { Box, createBox } from '@famly/mf_layout_box';
import { useDataProps } from '@famly/mf_ts-utils_data-aria-props';
import { Toast } from './toast';
import { getDefaultToastDuration } from './get-default-toast-duration';
import { useToastPortal } from './use-toast-portal';
/**
 * Toasts sit above everything, modals included: a modal that hides the error explaining why its
 * save failed leaves the user stuck with no way to find out. The portal host is in the top layer, so
 * this holds inside an open modal too, above an overlay that the modal opens with a higher z-index.
 */
const TOAST_Z_INDEX = 9999;
/**
 * A plain margin from the edge of the viewport. A host with chrome to clear passes its own.
 */
const DEFAULT_EDGE_OFFSET = 16;
const SIDE_MARGIN = 8;
/**
 * How long a toast takes to open or close, in milliseconds. Long enough to read as movement rather
 * than a jump, short enough not to hold up the message.
 */
const TRANSITION_DURATION = 450;
/**
 * The toasts currently on screen.
 *
 * The container is always in the DOM, even with nothing to show, so that a toast arriving is an
 * insertion into a region assistive technology is already watching.
 */
export const ToastGroup = (props) => {
    const { events, clearStatus, size, topOffset = DEFAULT_EDGE_OFFSET, bottomOffset = DEFAULT_EDGE_OFFSET } = props;
    const dataProps = useDataProps(props);
    const portal = useToastPortal();
    return createPortal(_jsxs(Box, { position: "fixed", zIndex: TOAST_Z_INDEX, right: SIDE_MARGIN, 
        /**
         * Narrow screens keep the toasts at the bottom, within reach of a thumb and clear of
         * the header, and let them span the width. Wider ones put them in the top corner at a
         * fixed width, where they do not cover the content being worked on.
         */
        left: { base: SIDE_MARGIN, tabletPortrait: 'auto' }, top: { base: 'auto', tabletPortrait: `calc(${topOffset}px + env(safe-area-inset-top))` }, bottom: {
            base: `calc(${bottomOffset}px + env(safe-area-inset-bottom))`,
            tabletPortrait: 'auto',
        }, width: { base: 'auto', tabletPortrait: '400px' }, ...dataProps, children: [['error', 'status'].map(kind => (_jsx(Announcement, { role: kind === 'error' ? 'alert' : 'status', "aria-live": kind === 'error' ? 'assertive' : 'polite', "aria-atomic": false, children: events
                    .filter(event => (kind === 'error' ? event.severity === 'error' : event.severity !== 'error'))
                    .map(event => (_jsxs(Box, { "aria-atomic": true, children: [event.message, event.occurences > 1 ? ` (${event.occurences})` : null] }, event.id))) }, kind))), _jsx(TransitionGroup, { children: events.map(event => (_jsx(Collapse, { timeout: TRANSITION_DURATION, children: _jsx(Box, { paddingY: 1, children: _jsx(ToastEventItem, { event: event, clearStatus: clearStatus, size: size }) }) }, event.id))) })] }), portal);
};
const Announcement = styled(createBox({
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    borderWidth: 0,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
})) `
    clip: rect(0, 0, 0, 0);
`;
const ToastEventItem = ({ event, clearStatus, size }) => {
    var _a;
    const dataProps = useDataProps(event);
    return (_jsx(Toast, { announce: false, message: event.message, severity: event.severity, size: size, occurences: event.occurences, onDismiss: () => clearStatus(event.id), duration: event.sticky ? undefined : ((_a = event.duration) !== null && _a !== void 0 ? _a : getDefaultToastDuration(event.severity, event.message)), ...dataProps, "data-e2e-class": "toast" }));
};
